import React, { useCallback, useLayoutEffect, useRef, FC } from 'react';
import cls from 'classnames';

import { LazyImgix } from 'src/explore/elements/LazyImgix';

import { IWebLink } from '../../../../../../../../../../../../types';
import Link from '../../../../../../../../atoms/Link';

import styles from './index.module.sass';

const sizeVertical = ( element?: HTMLElement ) => {
  if ( !element ) return '0';

  const elementStyle = getComputedStyle( element );
  return `${
    element.offsetHeight +
    parseInt( elementStyle.marginTop, 10 ) +
    parseInt( elementStyle.marginBottom, 10 )
  }px`;
};

interface DropdownProps {
  className?: string;
  isExpanded: boolean;
  links?: IWebLink[];
}

const Dropdown: FC<DropdownProps> = ({ className, isExpanded, links }) => {
  const containerRef = useRef<HTMLDivElement>( null );
  const contentRef = useRef<HTMLUListElement>( null );

  const size = useCallback(() => {
    const expandSize = sizeVertical( contentRef.current );
    const options = {
      duration: 200,
      easing: 'ease-out',
    };

    return {
      collapse: () =>
        containerRef.current?.animate([{ height: expandSize }, { height: 0 }], options ),
      expand: () => containerRef.current?.animate([{ height: 0 }, { height: expandSize }], options ),
    };
  }, []);

  useLayoutEffect(() => {
    if ( isExpanded ) {
      size().expand();
    } else {
      size().collapse();
    }
  }, [ isExpanded, size ]);

  return !links || links.length === 0 ? null : (
    <div
      className={cls( styles.container, { [styles.open]: isExpanded }, className )}
      ref={containerRef}
    >
      <ul className={cls( styles.content, { [styles.open]: isExpanded })} ref={contentRef}>
        {links.map(( link ) => (
          <li className={styles.item} key={link.url}>
            <Link className={styles.link} href={link.url}>
              {link.imageUrl && (
                <LazyImgix alt={link.label} height={48} src={link.imageUrl} width={48} />
              )}

              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default Dropdown;
